// src/components/KioskKeyboard.js
// TKT-2026-105056: an on-screen keyboard the kiosk draws itself.
//
// Kiosk tablets pop their own OS keyboard over the checkout dialog and cover the
// very fields the shopper is filling in. A keyboard rendered inside the page
// cannot do that, because it takes real layout space the dialog can size around.
//
// Off by default, per store. Enabled at Integrations, Kiosk, "Show an on-screen
// keyboard", which arrives here as themeOverrides.onScreenKeyboardEnabled.
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Paper } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    KeyboardBackspace as BackspaceIcon,
    KeyboardCapslock as ShiftIcon,
    SpaceBar as SpaceIcon,
    Check as DoneIcon,
} from '@mui/icons-material';

const LETTER_ROWS = [
    ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
    ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
    ['z', 'x', 'c', 'v', 'b', 'n', 'm'],
];

const SYMBOL_ROWS = [
    ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
    ['-', '_', '.', '+', '/', '#', '!', '$', '%', '&'],
    ['@', ',', "'", '*', '(', ')'],
];

const NUMERIC_ROWS = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['-', '0', '+'],
];

// Typing an address by hand on a touch keyboard is the slowest part of a kiosk
// checkout, so the email field gets its domains one tap away.
const EMAIL_SHORTCUTS = ['@gmail.com', '@yahoo.com', '@outlook.com', '@icloud.com', '@hotmail.com'];

/**
 * @param {object}   props
 * @param {'text'|'email'|'tel'} props.layout   which key set to draw
 * @param {(ch: string) => void} props.onKey    a printable character was pressed
 * @param {() => void}           props.onBackspace
 * @param {() => void}           props.onDone   dismiss the keyboard
 * @param {boolean}              props.shift    upper case for the next letter
 * @param {() => void}           props.onToggleShift
 * @param {boolean}              props.symbols  show numbers/symbols instead of letters
 * @param {() => void}           props.onToggleSymbols
 */
export default function KioskKeyboard({
    layout,
    onKey,
    onBackspace,
    onDone,
    shift,
    onToggleShift,
    symbols,
    onToggleSymbols,
}) {
    const numeric = layout === 'tel';

    const rows = useMemo(() => {
        if (numeric) return NUMERIC_ROWS;
        return symbols ? SYMBOL_ROWS : LETTER_ROWS;
    }, [numeric, symbols]);

    // preventDefault on press is what keeps the caret in the field being edited,
    // rather than the key stealing focus. tabIndex -1 keeps 42 keys out of the tab
    // order: this is a redundant control surface, and anyone tabbing has a real
    // keyboard and does not need it.
    const keyProps = (handler) => ({
        tabIndex: -1,
        onMouseDown: (e) => e.preventDefault(),
        onTouchStart: (e) => e.preventDefault(),
        onClick: handler,
    });

    const keySx = (theme) => ({
        minWidth: 0,
        flex: 1,
        height: 52,
        fontSize: '1.05rem',
        fontWeight: 600,
        textTransform: 'none',
        lineHeight: 1,
        color: theme.palette.text.primary,
        backgroundColor: alpha(theme.palette.text.primary, theme.palette.mode === 'light' ? 0.06 : 0.12),
        '&:hover': {
            backgroundColor: alpha(theme.palette.text.primary, theme.palette.mode === 'light' ? 0.12 : 0.2),
        },
    });

    return (
        <Paper
            elevation={0}
            role='group'
            aria-label='On-screen keyboard'
            sx={(theme) => ({
                mt: 2,
                p: 1,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                backgroundColor: alpha(theme.palette.background.default, theme.palette.mode === 'light' ? 0.7 : 0.4),
            })}
        >
            {layout === 'email' && !numeric && (
                <Box sx={{ display: 'flex', gap: 0.75, mb: 1, flexWrap: 'wrap' }}>
                    {EMAIL_SHORTCUTS.map((domain) => (
                        <Button
                            key={domain}
                            size='small'
                            variant='outlined'
                            sx={{ textTransform: 'none', fontWeight: 600, flex: '1 1 auto' }}
                            {...keyProps(() => onKey(domain))}
                        >
                            {domain}
                        </Button>
                    ))}
                </Box>
            )}

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.75,
                    ...(numeric ? { maxWidth: 320, mx: 'auto' } : {}),
                }}
            >
                {rows.map((row, rowIndex) => (
                    <Box key={`row-${rowIndex}`} sx={{ display: 'flex', gap: 0.75, justifyContent: 'center' }}>
                        {/* Shift sits on the last letter row, where a shopper expects it. */}
                        {!numeric && !symbols && rowIndex === LETTER_ROWS.length - 1 && (
                            <Button
                                sx={(t) => ({ ...keySx(t), flex: 1.4, color: shift ? t.palette.primary.onSurface : undefined })}
                                {...keyProps(onToggleShift)}
                            >
                                <ShiftIcon fontSize='small' />
                            </Button>
                        )}
                        {row.map((ch) => (
                            <Button
                                key={ch}
                                sx={keySx}
                                {...keyProps(() => onKey(shift && !numeric && !symbols ? ch.toUpperCase() : ch))}
                            >
                                {shift && !numeric && !symbols ? ch.toUpperCase() : ch}
                            </Button>
                        ))}
                        {((!numeric && !symbols && rowIndex === LETTER_ROWS.length - 1)
                            || (numeric && rowIndex === NUMERIC_ROWS.length - 1)
                            || (symbols && rowIndex === SYMBOL_ROWS.length - 1)) && (
                                <Button sx={(t) => ({ ...keySx(t), flex: 1.4 })} {...keyProps(onBackspace)}>
                                    <BackspaceIcon fontSize='small' />
                                </Button>
                            )}
                    </Box>
                ))}

                <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'center' }}>
                    {!numeric && (
                        <Button sx={(t) => ({ ...keySx(t), flex: 1.6 })} {...keyProps(onToggleSymbols)}>
                            {symbols ? 'ABC' : '123'}
                        </Button>
                    )}
                    {layout === 'email' && !numeric && (
                        <Button sx={keySx} {...keyProps(() => onKey('@'))}>@</Button>
                    )}
                    {!numeric && (
                        <Button sx={(t) => ({ ...keySx(t), flex: 4 })} {...keyProps(() => onKey(' '))}>
                            <SpaceIcon fontSize='small' />
                        </Button>
                    )}
                    {!numeric && <Button sx={keySx} {...keyProps(() => onKey('.'))}>.</Button>}
                    <Button
                        variant='contained'
                        sx={{ minWidth: 0, flex: numeric ? 1 : 1.6, height: 52, textTransform: 'none', fontWeight: 700 }}
                        {...keyProps(onDone)}
                    >
                        <DoneIcon fontSize='small' />
                    </Button>
                </Box>
            </Box>
        </Paper>
    );
}

KioskKeyboard.propTypes = {
    layout: PropTypes.oneOf(['text', 'email', 'tel']),
    onKey: PropTypes.func.isRequired,
    onBackspace: PropTypes.func.isRequired,
    onDone: PropTypes.func.isRequired,
    shift: PropTypes.bool,
    onToggleShift: PropTypes.func.isRequired,
    symbols: PropTypes.bool,
    onToggleSymbols: PropTypes.func.isRequired,
};

KioskKeyboard.defaultProps = {
    layout: 'text',
    shift: false,
    symbols: false,
};
